import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import {
  Check,
  ExternalLink,
  Github,
  Gitlab,
  GitPullRequestArrow,
  Link2,
  Loader2,
  RefreshCw,
  Trash2,
  Workflow,
} from 'lucide-react'
import RepositoryAttachModal from './RepositoryAttachModal'
import GitHistoryExplorer from './GitHistoryExplorer'
import {
  createRemoteRepoBinding,
  deleteRepoBinding,
  getRepoBinding,
  listRepoSyncJobs,
  publishRepoMergeRequest,
  publishRepoPullRequest,
  preflightShareForReview,
  preflightSyncLatestChanges,
  triggerRepoSync,
  upsertRepoBinding,
  type CreateRemoteRepoPayload,
  type GitHubRepo,
  type GitLabRepo,
  type RepoBinding,
  type RepoPreflightResponse,
  type RepoSyncJob,
} from '../../lib/api'
import { formatRelativeTime } from '../../lib/format'

function providerLabel(provider: string): string {
  return provider === 'gitlab' ? 'GitLab' : 'GitHub'
}

function syncStatusClass(status: string): string {
  switch (status) {
    case 'success':
      return 'border-green-500/30 bg-green-500/10 text-green-500'
    case 'queued':
    case 'running':
      return 'border-blue-500/30 bg-blue-500/10 text-blue-500'
    case 'failed':
    case 'error':
      return 'border-red-500/30 bg-red-500/10 text-red-500'
    default:
      return 'border-border bg-secondary text-muted-foreground'
  }
}

function divergenceClass(status: string): string {
  switch (status) {
    case 'synced':
      return 'border-green-500/30 bg-green-500/10 text-green-500'
    case 'diverged':
      return 'border-amber-500/30 bg-amber-500/10 text-amber-500'
    default:
      return 'border-border bg-secondary text-muted-foreground'
  }
}

interface RepositoryPanelProps {
  appName: string
  mode?: 'detail' | 'workspace' | 'embedded'
  canManage?: boolean
  hideHistory?: boolean
  onBindingChange?: (binding: RepoBinding | null) => void
}

interface PendingConfirmAction {
  title: string
  description: string
  confirmLabel: string
  loadingLabel: string
  variant?: 'default' | 'destructive'
  onConfirm: () => Promise<void>
}

export default function RepositoryPanel({
  appName,
  mode = 'detail',
  canManage = true,
  hideHistory = false,
  onBindingChange,
}: RepositoryPanelProps) {
  const [binding, setBinding] = useState<RepoBinding | null>(null)
  const [jobs, setJobs] = useState<RepoSyncJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showAttachModal, setShowAttachModal] = useState(false)
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false)
  const [reviewPreflight, setReviewPreflight] = useState<RepoPreflightResponse | null>(null)
  const [confirmAction, setConfirmAction] = useState<PendingConfirmAction | null>(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  // Auto-save state for the automation toggle (D-30 / UX-16). Shows a brief "Saved" indicator
  // after the debounced save resolves successfully.
  const [autoSaveJustSaved, setAutoSaveJustSaved] = useState(false)
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoSaveSavedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear in-dialog error when a new confirm action opens (D-35).
  useEffect(() => {
    setConfirmError(null)
  }, [confirmAction])

  const runConfirmAction = useCallback(async () => {
    if (!confirmAction) return
    setConfirmLoading(true)
    setConfirmError(null)
    try {
      await confirmAction.onConfirm()
      setConfirmAction(null)
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : 'Action failed')
      // Keep dialog open so the user can retry or cancel (D-09).
    } finally {
      setConfirmLoading(false)
    }
  }, [confirmAction])

  const formatPreflightMessage = useCallback((
    title: string,
    response: RepoPreflightResponse,
    confirmLabel: string,
    explanation?: string,
  ): string => {
    const warnings = response.warnings
      .map((warning) => `- ${warning.message}`)
      .join('\n')
    const sections: Array<string | null> = [
      title,
      '',
      explanation ?? null,
      explanation ? '' : null,
      response.summary,
      warnings ? `\nPlease note:\n${warnings}` : '',
      '',
      confirmLabel,
    ]
    return sections.filter((line): line is string => line !== null).join('\n')
  }, [])

  const openRepoConfirm = useCallback(async (
    title: string,
    confirmLabel: string,
    loadingLabel: string,
    loadPreflight: () => Promise<RepoPreflightResponse>,
    fallbackSummary: string,
    onConfirm: () => Promise<void>,
    variant: 'default' | 'destructive' = 'default',
    explanation?: string,
  ): Promise<void> => {
    let description: string
    try {
      const response = await loadPreflight()
      description = formatPreflightMessage(title, response, confirmLabel, explanation)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The safety check could not run.'
      const sections: Array<string | null> = [
        title,
        '',
        explanation ?? null,
        explanation ? '' : null,
        fallbackSummary,
        '',
        `Safety check unavailable: ${message}`,
        '',
        confirmLabel,
      ]
      description = sections.filter((line): line is string => line !== null).join('\n')
    }
    setConfirmAction({ title, description, confirmLabel, loadingLabel, variant, onConfirm })
  }, [formatPreflightMessage])

  function reviewRequestUrl(response: RepoPreflightResponse | null): string | null {
    // `reviewRequest` is a free-form dict whose nested keys may be snake_case
    // (GitHub/GitLab provider API shapes, not platform shapes — left unchanged
    // per the UX-09 scope carve-out).
    const request = response?.reviewRequest
    if (!request) return null
    const url = request.url ?? request.pr_url ?? request.mr_url ?? request.html_url
    return typeof url === 'string' ? url : null
  }

  function reviewRequestLabel(response: RepoPreflightResponse | null): string | null {
    const request = response?.reviewRequest
    if (!request) return null
    const number = request.number ?? request.pr_number ?? request.mr_iid ?? request.id
    if (typeof number === 'number' || typeof number === 'string') {
      return `Review request ${number}`
    }
    return 'Review request ready'
  }

  const loadRepositoryState = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [nextBinding, nextJobs] = await Promise.all([
        getRepoBinding(appName),
        listRepoSyncJobs(appName, 8),
      ])
      setBinding(nextBinding)
      onBindingChange?.(nextBinding)
      setJobs(nextJobs)
      if (nextBinding) {
        const nextPreflight = await preflightShareForReview(appName).catch(() => null)
        setReviewPreflight(nextPreflight)
      } else {
        setReviewPreflight(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repository details')
    } finally {
      setLoading(false)
    }
  }, [appName, onBindingChange])

  useEffect(() => {
    void loadRepositoryState()
  }, [loadRepositoryState])

  useEffect(() => {
    if (!binding) {
      setAutoSyncEnabled(false)
      return
    }
    setAutoSyncEnabled(binding.syncMode === 'linked' && binding.webhookEnabled)
  }, [binding])

  const activeSyncJob = useMemo(
    () => jobs.find((job) => job.status === 'queued' || job.status === 'running') || null,
    [jobs],
  )
  const latestFailedSyncJob = useMemo(
    () => jobs.find((job) => job.status === 'failed' || job.status === 'error') || null,
    [jobs],
  )

  useEffect(() => {
    if (!activeSyncJob) return

    const intervalId = window.setInterval(() => {
      void loadRepositoryState()
    }, 3000)
    return () => window.clearInterval(intervalId)
  }, [activeSyncJob, loadRepositoryState])

  const providerIcon = useMemo(() => {
    if (binding?.provider === 'gitlab') return Gitlab
    return Github
  }, [binding?.provider])

  const handleAttachGitHub = useCallback(async (repo: GitHubRepo, branch: string) => {
    setActionLoading('attach-github')
    setError(null)
    setNotice(null)
    try {
      const nextBinding = await upsertRepoBinding(appName, {
        provider: 'github',
        repo_full_name: repo.full_name,
        default_branch: repo.default_branch,
        tracked_branch: branch,
        sync_mode: 'manual',
        webhook_enabled: false,
      })
      setBinding(nextBinding)
      onBindingChange?.(nextBinding)
      setShowAttachModal(false)
      setNotice(`Connected ${repo.full_name}`)
      await loadRepositoryState()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link GitHub repository')
    } finally {
      setActionLoading(null)
    }
  }, [appName, loadRepositoryState, onBindingChange])

  const handleAttachGitLab = useCallback(async (repo: GitLabRepo, branch: string) => {
    setActionLoading('attach-gitlab')
    setError(null)
    setNotice(null)
    try {
      const nextBinding = await upsertRepoBinding(appName, {
        provider: 'gitlab',
        repo_full_name: repo.full_name,
        default_branch: repo.default_branch,
        tracked_branch: branch,
        sync_mode: 'manual',
        webhook_enabled: false,
      })
      setBinding(nextBinding)
      onBindingChange?.(nextBinding)
      setShowAttachModal(false)
      setNotice(`Connected ${repo.full_name}`)
      await loadRepositoryState()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link GitLab repository')
    } finally {
      setActionLoading(null)
    }
  }, [appName, loadRepositoryState, onBindingChange])

  const handleCreateRemote = useCallback(async (payload: CreateRemoteRepoPayload) => {
    const provider = payload.provider
    setActionLoading(`create-${provider}`)
    setError(null)
    setNotice(null)
    try {
      const result = await createRemoteRepoBinding(appName, payload)
      setBinding(result.binding)
      onBindingChange?.(result.binding)
      setShowAttachModal(false)
      setNotice(`Created and connected ${result.repository.repoFullName}`)
      await loadRepositoryState()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to create ${providerLabel(provider)} repository`)
    } finally {
      setActionLoading(null)
    }
  }, [appName, loadRepositoryState, onBindingChange])

  const handleDetach = useCallback(() => {
    if (!binding) return
    setError(null)
    setNotice(null)
    setConfirmAction({
      title: `Detach ${binding.repoFullName}?`,
      description: [
        `Disconnect ${binding.repoFullName} from this app.`,
        '',
        'Workspace will keep its current files but stop syncing from the remote.',
        'Any in-flight sync jobs will be cancelled.',
        'Webhooks on the remote repository will be removed.',
        'Sync history is kept for reference.',
        'You can re-link any repository at any time.',
      ].join('\n'),
      confirmLabel: 'Detach',
      loadingLabel: 'Detaching...',
      variant: 'destructive',
      onConfirm: async () => {
        setActionLoading('detach')
        try {
          await deleteRepoBinding(appName)
          setBinding(null)
          onBindingChange?.(null)
          setJobs([])
          setNotice('Repository connection removed')
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to detach repository'
          setError(message)
          throw err
        } finally {
          setActionLoading(null)
        }
      },
    })
  }, [appName, binding, onBindingChange])

  const handleManualSync = useCallback(() => {
    if (!binding) return
    if (activeSyncJob) {
      setNotice(`Update #${activeSyncJob.id} is already ${activeSyncJob.status}`)
      return
    }
    setError(null)
    setNotice(null)
    const fallbackSummary = binding.lastSyncAt
      ? 'This checks the connected repository and brings the latest selected changes into this workspace.'
      : 'This is the first update for this connection. The latest selected changes will be brought into this workspace.'
    void openRepoConfirm(
      'Get the latest changes?',
      'Continue and get the latest changes?',
      'Updating...',
      () => preflightSyncLatestChanges(appName),
      fallbackSummary,
      async () => {
        setActionLoading('sync')
        try {
          const job = await triggerRepoSync(appName, { trigger: 'manual' })
          setNotice(`Update #${job.id} queued`)
          await loadRepositoryState()
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to start update'
          setError(message)
          throw err
        } finally {
          setActionLoading(null)
        }
      },
    )
  }, [activeSyncJob, appName, binding, loadRepositoryState, openRepoConfirm])

  const handlePublishPr = useCallback(() => {
    if (!binding) return
    const isUpdate = reviewPreflight?.action === 'update_review_request'
    setError(null)
    setNotice(null)
    const shareExplanation = binding.provider === 'github'
      ? 'Creates a draft pull request on the connected GitHub repository so reviewers can comment before changes go live. Saved changes from this workspace are pushed; unsaved edits are not included.'
      : 'Creates a draft merge request on the connected GitLab project so reviewers can comment before changes go live. Saved changes from this workspace are pushed; unsaved edits are not included.'
    void openRepoConfirm(
      isUpdate ? 'Update review request?' : 'Share for review?',
      isUpdate ? 'Continue and update the review request?' : 'Continue and share for review?',
      isUpdate ? 'Updating...' : 'Sharing...',
      () => preflightShareForReview(appName),
      'This will share saved app changes with the connected repository for review.',
      async () => {
        setActionLoading('publish-change')
        try {
          if (binding.provider === 'github') {
            const result = await publishRepoPullRequest(appName, { draft: true })
            setNotice(
              result.publication.prUrl
                ? `Review request ready: ${result.publication.prUrl}`
                : `Review request #${result.publication.prNumber ?? ''} created`
            )
            if (result.publication.prUrl) {
              window.open(result.publication.prUrl, '_blank', 'noopener,noreferrer')
            }
          } else {
            const result = await publishRepoMergeRequest(appName, { draft: true })
            setNotice(
              result.publication.mrUrl
                ? `Review request ready: ${result.publication.mrUrl}`
                : `Review request !${result.publication.mrIid ?? ''} created`
            )
            if (result.publication.mrUrl) {
              window.open(result.publication.mrUrl, '_blank', 'noopener,noreferrer')
            }
          }
          await loadRepositoryState()
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to share for review'
          const friendly = message.includes('allow_empty_commit')
            ? 'No saved changes to share for review. Open Advanced History for existing technical versions.'
            : message
          setError(friendly)
          throw new Error(friendly)
        } finally {
          setActionLoading(null)
        }
      },
      'default',
      shareExplanation,
    )
  }, [appName, binding, loadRepositoryState, openRepoConfirm, reviewPreflight?.action])

  const handleSaveAutomation = useCallback(async (nextAutoSyncEnabled: boolean) => {
    if (!binding) return

    setActionLoading('save-automation')
    setError(null)
    setNotice(null)
    try {
      const nextBinding = await upsertRepoBinding(appName, {
        provider: binding.provider as 'github' | 'gitlab',
        repo_full_name: binding.repoFullName,
        default_branch: binding.defaultBranch,
        tracked_branch: binding.trackedBranch,
        sync_mode: nextAutoSyncEnabled ? 'linked' : 'manual',
        webhook_enabled: nextAutoSyncEnabled,
      })
      setBinding(nextBinding)
      onBindingChange?.(nextBinding)
      setNotice(
        nextAutoSyncEnabled
          ? 'Automatic updates enabled'
          : 'Manual updates enabled'
      )
      // Show an inline "Saved" indicator briefly (D-30 / UX-16).
      setAutoSaveJustSaved(true)
      if (autoSaveSavedTimeoutRef.current) {
        clearTimeout(autoSaveSavedTimeoutRef.current)
      }
      autoSaveSavedTimeoutRef.current = setTimeout(() => {
        setAutoSaveJustSaved(false)
        autoSaveSavedTimeoutRef.current = null
      }, 2500)
      await loadRepositoryState()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update settings')
    } finally {
      setActionLoading(null)
    }
  }, [appName, binding, loadRepositoryState, onBindingChange])

  // Debounced auto-save trigger for the Manual/Automatic toggle.
  const scheduleAutomationAutoSave = useCallback((nextAutoSyncEnabled: boolean) => {
    setAutoSyncEnabled(nextAutoSyncEnabled)
    setAutoSaveJustSaved(false)
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null
      void handleSaveAutomation(nextAutoSyncEnabled)
    }, 500)
  }, [handleSaveAutomation])

  // Clean up pending timers on unmount.
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current)
      if (autoSaveSavedTimeoutRef.current) clearTimeout(autoSaveSavedTimeoutRef.current)
    }
  }, [])

  const wrapperClass = mode === 'workspace'
    ? 'h-full overflow-y-auto bg-card p-4 space-y-6'
    : mode === 'embedded'
      ? 'space-y-6'
      : 'rounded-xl border border-border bg-card p-6 space-y-6'

  const ProviderIcon = providerIcon
  const publishLabel = reviewPreflight?.action === 'update_review_request'
    ? 'Update review request'
    : 'Share for review'
  const visibleReviewRequestUrl = reviewRequestUrl(reviewPreflight)
  const visibleReviewRequestLabel = reviewRequestLabel(reviewPreflight)
  const remoteRepoUrl = binding?.repoWebUrl || null
  const syncButtonLabel = activeSyncJob
    ? activeSyncJob.status === 'queued' ? 'Sync queued' : 'Sync running'
    : 'Get latest'

  return (
    <div className={wrapperClass}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Remote</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Connect GitHub or GitLab to keep a copy outside CC, ask for review, or bring updates into this workspace.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowAttachModal(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary"
          >
            <Link2 className="w-4 h-4" />
            {binding ? 'Change connection' : 'Connect repository'}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {notice && (
        <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400">
          {notice}
        </div>
      )}

      {loading && !binding ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/20 p-4 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading remote details...
        </div>
      ) : binding ? (
        <div className="space-y-4 rounded-lg border border-border bg-secondary/10 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-lg border border-border bg-card p-2">
                <ProviderIcon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {remoteRepoUrl ? (
                    <a
                      href={remoteRepoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-w-0 max-w-full items-center gap-1 font-medium text-primary hover:underline"
                    >
                      <span className="truncate">{binding.repoFullName}</span>
                      <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    </a>
                  ) : (
                    <p className="break-all font-medium">{binding.repoFullName}</p>
                  )}
                  <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                    {providerLabel(binding.provider)}
                  </span>
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${syncStatusClass(binding.lastSyncStatus)}`}>
                    {binding.lastSyncStatus === 'success' ? 'Updated' : binding.lastSyncStatus}
                  </span>
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${divergenceClass(binding.divergenceStatus)}`}>
                    {binding.divergenceStatus === 'synced' ? 'Up to date' : binding.divergenceStatus}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Main shared version: <span className="font-medium">{binding.defaultBranch}</span>
                </p>
                {binding.lastSyncMessage && (
                  <p className="mt-2 text-sm text-muted-foreground">{binding.lastSyncMessage}</p>
                )}
                {visibleReviewRequestLabel && (
                  <p className="mt-2 text-sm">
                    {visibleReviewRequestUrl ? (
                      <a
                        href={visibleReviewRequestUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        {visibleReviewRequestLabel}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">{visibleReviewRequestLabel}</span>
                    )}
                  </p>
                )}
              </div>
            </div>

            {canManage && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => void loadRepositoryState()}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh status
                </button>
                <button
                  type="button"
                  onClick={() => void handleManualSync()}
                  disabled={!!actionLoading || !!activeSyncJob}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
                >
                  {actionLoading === 'sync' || activeSyncJob ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Workflow className="w-4 h-4" />
                  )}
                  {syncButtonLabel}
                </button>
                <button
                  type="button"
                  onClick={() => void handlePublishPr()}
                  disabled={!!actionLoading}
                  title={
                    binding.provider === 'github'
                      ? 'Creates a draft pull request on the connected GitHub repository so reviewers can comment before changes go live.'
                      : 'Creates a draft merge request on the connected GitLab project so reviewers can comment before changes go live.'
                  }
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
                >
                  {actionLoading === 'publish-change' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <GitPullRequestArrow className="w-4 h-4" />
                  )}
                  {publishLabel}
                </button>
                <button
                  type="button"
                  onClick={() => void handleDetach()}
                  disabled={!!actionLoading}
                  className="inline-flex items-center gap-2 rounded-lg border border-destructive/30 px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                >
                  {actionLoading === 'detach' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Detach
                </button>
              </div>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-lg border border-border bg-card p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Last updated</p>
              <p className="mt-1 font-medium">{formatRelativeTime(binding.lastSyncAt)}</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Remote version</p>
              <p className="mt-1 font-mono text-xs">{binding.lastSeenSha?.slice(0, 12) || 'Unknown'}</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Workspace version</p>
              <p className="mt-1 font-mono text-xs">{(binding.lastSyncedVersionId || binding.lastDeployedSha)?.slice(0, 12) || 'Unknown'}</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Automatic updates</p>
              <p className="mt-1 font-medium">{binding.webhookEnabled ? 'On' : 'Off'}</p>
              {binding.webhookId && (
                <p className="mt-1 font-mono text-xs text-muted-foreground">{binding.webhookId}</p>
              )}
            </div>
          </div>

          {(activeSyncJob || latestFailedSyncJob) && (
            <div
              className={`rounded-lg border p-3 text-sm ${
                activeSyncJob
                  ? 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300'
                  : 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
              }`}
            >
              {activeSyncJob ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span>
                    Update #{activeSyncJob.id} is {activeSyncJob.status}. This panel refreshes automatically.
                  </span>
                  <button
                    type="button"
                    onClick={() => void loadRepositoryState()}
                    disabled={loading}
                    className="rounded-lg border border-current/30 px-3 py-1 text-xs transition-colors hover:bg-current/10 disabled:opacity-50"
                  >
                    Refresh now
                  </button>
                </div>
              ) : latestFailedSyncJob ? (
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <span>
                    Last update failed
                    {latestFailedSyncJob.errorMessage ? `: ${latestFailedSyncJob.errorMessage}` : ''}
                  </span>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => void handleManualSync()}
                      disabled={!!actionLoading}
                      className="rounded-lg border border-current/30 px-3 py-1 text-xs transition-colors hover:bg-current/10 disabled:opacity-50"
                    >
                      Try again
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          )}

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">Updates from remote</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Choose whether this workspace gets remote updates manually or automatically.
                </p>
              </div>
              {/* Auto-save status indicator (D-30 / UX-16) — replaces the manual save button. */}
              {canManage && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
                  aria-live="polite"
                >
                  {actionLoading === 'save-automation' ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Saving...
                    </>
                  ) : autoSaveJustSaved ? (
                    <>
                      <Check className="w-3 h-3 text-green-500" />
                      Saved
                    </>
                  ) : null}
                </span>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => scheduleAutomationAutoSave(false)}
                disabled={!canManage || !!actionLoading}
                className={`rounded-lg border px-3 py-2 text-sm transition-colors disabled:opacity-50 ${
                  !autoSyncEnabled ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-secondary'
                }`}
              >
                Manual
              </button>
              <button
                type="button"
                onClick={() => scheduleAutomationAutoSave(true)}
                disabled={!canManage || !!actionLoading}
                className={`rounded-lg border px-3 py-2 text-sm transition-colors disabled:opacity-50 ${
                  autoSyncEnabled ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-secondary'
                }`}
              >
                Automatic
              </button>
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              Automatic updates require repository admin access. Changes save automatically.
            </p>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold">Recent updates</h3>
              <span className="text-xs text-muted-foreground">{jobs.length} shown</span>
            </div>
            {jobs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No updates yet.</p>
            ) : (
              <div className="space-y-2">
                {jobs.map((job) => (
                  <div key={job.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">Update #{job.id}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-xs ${syncStatusClass(job.status)}`}>
                          {job.status}
                        </span>
                        <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                          {job.trigger}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Created {formatRelativeTime(job.createdAt)}
                      </p>
                      {job.errorMessage && (
                        <p className="mt-1 text-xs text-destructive">{job.errorMessage}</p>
                      )}
                    </div>
                    {job.status === 'success' && binding.provider === 'github' && (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <ExternalLink className="w-3 h-3" />
                        Ready for review
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-secondary/10 p-5">
          <div className="flex items-start gap-3">
            <div className="rounded-lg border border-border bg-card p-2">
              <Link2 className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium">Connect a code repository</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Connect GitHub or GitLab to keep a copy outside CC, ask for review, or bring updates into this workspace.
              </p>
            </div>
          </div>
        </div>
      )}

      {!hideHistory && (
        <GitHistoryExplorer
          appName={appName}
          binding={binding}
          initialLimit={mode === 'workspace' ? 140 : 80}
        />
      )}

      {showAttachModal && (
        <RepositoryAttachModal
          appName={appName}
          initialProvider={binding?.provider === 'gitlab' ? 'gitlab' : 'github'}
          submitting={
            actionLoading === 'attach-github'
            || actionLoading === 'attach-gitlab'
            || actionLoading === 'create-github'
            || actionLoading === 'create-gitlab'
          }
          onClose={() => {
            if (!actionLoading) {
              setShowAttachModal(false)
            }
          }}
          onCreateRemote={handleCreateRemote}
          onAttachGitHub={handleAttachGitHub}
          onAttachGitLab={handleAttachGitLab}
        />
      )}

      <AlertDialog.Root
        open={!!confirmAction}
        onOpenChange={(open) => {
          if (confirmLoading) return
          if (!open) setConfirmAction(null)
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-lg">
            <AlertDialog.Title className="text-lg font-semibold mb-2">
              {confirmAction?.title}
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-muted-foreground whitespace-pre-wrap mb-4">
              {confirmAction?.description}
            </AlertDialog.Description>
            {confirmError && (
              <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {confirmError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
                  disabled={confirmLoading}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <button
                type="button"
                onClick={runConfirmAction}
                disabled={confirmLoading}
                className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                  confirmAction?.variant === 'destructive'
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
              >
                {confirmLoading
                  ? confirmAction?.loadingLabel
                  : confirmError
                    ? 'Try again'
                    : confirmAction?.confirmLabel}
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  )
}
